import React from 'react';
import { Button, Spinner, Text } from '@spark';

/** `data-walkthrough` value that marks the wizard's real Next button (tests and the demo tour find it by this). */
export const WIZARD_NEXT_WALKTHROUGH_TARGET = 'wizard-next';

/** Accessible name of the compact loader the guided demo tour shows on Next while it turns the page. */
export const WIZARD_NEXT_DEMO_LOADING_LABEL = 'Opening the next step';

/**
 * Primary Next/Train action used by the sticky heading and by the DOM-last
 * overlay on `nextInHeading={false}` steps. Same size in idle and `loading`
 * so the overlay cannot grow over Back.
 *
 * `demoLoading` is the guided demo tour's half-second "turning the page"
 * state (Figma 9455:6844): the label gives way to a spinning loader in the
 * primary colours. The button only shrinks, so the right-aligned overlay
 * stays clear of Back.
 *
 * `walkthroughTarget` (default `wizard-next`) is the walkthrough anchor; pass
 * `null` for a decorative copy so the coach mark only ever points at the
 * real button.
 */
export const WizardNextButton = ({
    onClick,
    disabled = false,
    loading = false,
    demoLoading = false,
    label = 'Next',
    loadingLabel = 'Loading…',
    disableWhenReadOnly = false,
    walkthroughTarget = WIZARD_NEXT_WALKTHROUGH_TARGET,
}) => (
    <Button
        variant="primary"
        size="medium"
        disabled={disabled || loading}
        disableWhenReadOnly={disableWhenReadOnly}
        loading={demoLoading}
        loadingLabel={WIZARD_NEXT_DEMO_LOADING_LABEL}
        startIcon={loading ? <Spinner size="small" /> : undefined}
        onClick={onClick}
        data-walkthrough={walkthroughTarget ?? undefined}
    >
        {loading ? loadingLabel : label}
    </Button>
);

/**
 * Shared chrome for wizard steps 2-5 and the task Model Summary tab (Figma
 * 3001-68512): task name above the step title on the left, optional Back
 * (secondary) + primary action on the right. `taskName` may be briefly empty
 * on a deep-linked resume until the usecase loads — the step title then
 * renders alone.
 *
 * `nextInHeading={false}`: the step renders the real Next button itself as
 * its LAST DOM child (so Tab reaches it after the whole step) and overlays
 * it onto this heading via the `.cw-step--next-last` grid; the heading then
 * renders only an invisible same-sized placeholder to reserve the slot so
 * Back keeps its position.
 *
 * `nextDemoLoading` is the demo tour's compact loader on Next (see
 * `WizardNextButton`); the placeholder keeps the idle size so Back stays put.
 */
const WizardStepHeading = ({
    title,
    taskName = null,
    onBack,
    onNext,
    nextLabel = 'Next',
    nextDisabled = false,
    nextLoading = false,
    nextDemoLoading = false,
    nextLoadingLabel = 'Loading…',
    nextDisableWhenReadOnly = false,
    nextInHeading = true,
    errorMessage = null,
    className = 'cw-step-heading',
}) => (
    <>
        <div className={`${className} fs-unmask`}>
            <div className={`${className}__titles`}>
                {taskName && (
                    <Text variant="heading-3-semibold" color="primary" as="h2">
                        {taskName}
                    </Text>
                )}
                <Text variant="heading-4-semibold" color="primary" as="h3">
                    {title}
                </Text>
            </div>
            <div className={`${className}__actions`}>
                {onBack && (
                    <Button variant="secondary" size="medium" onClick={onBack}>
                        Back
                    </Button>
                )}
                {onNext && nextInHeading && (
                    <WizardNextButton
                        onClick={onNext}
                        disabled={nextDisabled}
                        loading={nextLoading}
                        demoLoading={nextDemoLoading}
                        label={nextLabel}
                        loadingLabel={nextLoadingLabel}
                        disableWhenReadOnly={nextDisableWhenReadOnly}
                    />
                )}
                {onNext && !nextInHeading && (
                    <span className="cw-step-heading__next-placeholder" aria-hidden="true">
                        <WizardNextButton
                            disabled
                            loading={nextLoading}
                            label={nextLabel}
                            loadingLabel={nextLoadingLabel}
                            disableWhenReadOnly={nextDisableWhenReadOnly}
                            walkthroughTarget={null}
                        />
                    </span>
                )}
            </div>
        </div>
        {errorMessage && (
            <div className={`${className}__error fs-unmask`}>{errorMessage}</div>
        )}
    </>
);

export default WizardStepHeading;
